import diff from 'immutablediff'
import {
  all,
  call,
  put,
  race,
  takeEvery,
  takeLatest,
  take,
} from 'redux-saga/effects'

import { asApiError, handleError } from 'api/api-utils'
import reportSelectionApi from 'api/ReportSelectionApi'

import {
  saveReportSelectionError,
  saveReportSelectionSuccess,
  getReportProgressForPreviewSuccess,
  getReportDataPreviewSuccess,
  getReportDataPreviewError,
  reportDataPreviewWaitingForCalculation,
} from './actions'
import ReportSelectionUpdateRecord from 'records/ReportSelection/report-selection-update'
import {
  OPEN_REPORT_WITHOUT_SAVING,
  SAVE_REPORT,
  GET_REPORT_DATA_PREVIEW,
  CLEAR_REPORT_DATA_PREVIEW,
} from './constants'
import { eventChannel, END } from 'redux-saga'
import {
  readUntilCalculationFinished,
  recordCalculationRefusal,
} from 'utils/actualsCalculationWait'

export function* saveReportSelection(action) {
  const {
    customerCode,
    companyCode,
    oldReportSelection,
    newReportSelection,
  } = action
  try {
    const currentReport = new ReportSelectionUpdateRecord(oldReportSelection)
    const incomingReport = new ReportSelectionUpdateRecord(newReportSelection)
    const patch = diff(currentReport, incomingReport)
    const responseValue = yield call(reportSelectionApi.patchReportSelection, {
      customerCode,
      companyCode,
      patch,
      reportSelectionId: currentReport.get('id'),
    })
    yield put(
      saveReportSelectionSuccess({
        customerCode,
        companyCode,
        reportSelection: responseValue,
      })
    )
  } catch (error) {
    yield put(handleError(error, saveReportSelectionError))
  }
}

export function* getReportDataPreview(action) {
  try {
    // Navigating away (or closing the preview) dispatches CLEAR_REPORT_DATA_PREVIEW. That must end
    // the wait here: `loading` in the ReportSelections domain is one flat flag shared by every
    // editor, so a wait abandoned by its own view would otherwise keep looping - and keep that flag
    // stuck true - for whichever editor opens next.
    yield race({
      read: call(
        readUntilCalculationFinished,
        (outcome) => readReportDataPreviewOnce(action, outcome),
        {
          waiting: (companies) =>
            reportDataPreviewWaitingForCalculation({ companies }),
          giveUp: (frame) =>
            handleError(asApiError(frame), getReportDataPreviewError),
        }
      ),
      cancelled: take(CLEAR_REPORT_DATA_PREVIEW),
    })
  } catch (error) {
    yield put(handleError(error, getReportDataPreviewError))
  }
}

/**
 * Reads the preview once, reporting back by writing into `outcome`:
 * `outcome.calculationInProgress` is set to `{ error, companies }` when the API refused because an
 * actuals calculation is writing the figures of one of the report's companies, which is the
 * caller's cue to ask again, and left unset when the read finished - with data, or with an error
 * that has already been dispatched.
 *
 * It cannot be a return value - see readReportDataOnce in ReportDatas/sagas.js: the channel is
 * closed with END once the read is over, and redux-saga answers END by terminating this generator
 * at `take` rather than resuming it, so nothing after the loop runs.
 */
export function* readReportDataPreviewOnce(action, outcome) {
  const { companyCode, customerCode, reportSelection } = action

  const channel = eventChannel((emitter) => {
    let reportRows = []
    let metadata = null
    const onMessage = (event) => {
      try {
        const parsed = JSON.parse(event.data)

        if (event.event === 'metadata') {
          metadata = parsed
          return
        }

        if (event.event === 'data-batch') {
          // Backend double-serializes: parse the inner JSON string containing the batch array
          const batchItems = JSON.parse(parsed.items)
          reportRows.push(...batchItems) // Spread to add all items from batch
          return
        }

        if (event.event === 'data-complete') {
          emitter(
            getReportDataPreviewSuccess({
              report: reportRows,
              exchangeRates: metadata?.metadata?.exchangeRates,
            })
          )
          reportRows = []
          metadata = null
          emitter(END)
          return
        }
      } catch (error) {
        emitter(handleError(error, getReportDataPreviewError))
        emitter(END)
      }
    }

    const onProgress = (event) => {
      emitter(
        getReportProgressForPreviewSuccess({
          progress: event.data,
          companyCode,
          customerCode,
        })
      )
    }

    const onError = (event) => {
      const { refused, parsedError } = recordCalculationRefusal(
        event,
        outcome,
        emitter
      )
      if (refused) return

      // Wrapped the way every other error path arrives - ErrorComponent reads props.data, and
      // handing over the parsed body on its own renders a bare "Something went wrong" with
      // neither the server's message nor its code.
      emitter(handleError(asApiError(parsedError), getReportDataPreviewError))
      emitter(END)
    }

    reportSelectionApi.openReportByReportSelection({
      companyCode,
      customerCode,
      reportSelection,
      onMessage,
      onProgress,
      onError,
    })

    return () => {}
  })

  // No exit of its own: END terminates this generator where it waits, so the loop is left by
  // termination and never by falling out of it.
  while (true) {
    const event = yield take(channel)
    yield put(event)
  }
}

export function* reportSelectionsSaga() {
  yield all([
    // takeLatest, not takeEvery: a wait for a calculation to finish can run for up to
    // CALCULATION_MAX_TOTAL_WAIT_MS (10 minutes). If the user opens another preview while an
    // older one is still waiting, takeEvery would let both run and the abandoned one could still
    // overwrite the editor with stale rows once it finally resolves. takeLatest cancels it instead.
    // One watcher for both actions: a preview started by either must cancel one started by the other.
    takeLatest(
      [GET_REPORT_DATA_PREVIEW, OPEN_REPORT_WITHOUT_SAVING],
      getReportDataPreview
    ),
    takeEvery(SAVE_REPORT, saveReportSelection),
  ])
}

// All sagas to be loaded
export default reportSelectionsSaga
